import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Check, Package, Wrench, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { publicAPI } from '../services/api';
import { formatWhatsAppLink, getDefaultQuoteMessage } from '../utils/whatsapp';
import { resolveImageUrl } from '../utils/imageUrl';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    publicAPI.getProduct(id)
      .then(data => { setProduct(data); setLoading(false); })
      .catch(() => { setProduct(null); setLoading(false); });
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Header /><div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div><Footer /></div>
  );

  if (!product) return (
    <div className="min-h-screen bg-gray-50"><Header />
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h2 className="text-3xl font-bold text-gray-600 mb-4">Produto não encontrado</h2>
        <Button onClick={() => navigate('/produtos')} className="bg-[#055509]">Voltar para Produtos</Button>
      </div><Footer /></div>
  );

  const images = product.images?.length > 0 ? product.images : [product.mainImage].filter(Boolean);
  const currentImage = resolveImageUrl(images[selectedImage] || images[0]);

  const handleQuoteClick = () => {
    window.open(formatWhatsAppLink(getDefaultQuoteMessage(product.code, product.name)), '_blank');
  };

  // Spec display labels
  const specLabels = {
    displacement: 'Deslocamento', maxPressure: 'Pressão Máxima', maxSpeed: 'Velocidade Máxima',
    maxTorque: 'Torque Máximo', fluidType: 'Tipo de Fluido', flow: 'Vazão',
    connection: 'Conexão', material: 'Material', volume: 'Volume', preCharge: 'Pré-carga',
    motorPower: 'Potência Motor', voltage: 'Voltagem', tankCapacity: 'Capacidade Tanque',
    pumpFlow: 'Vazão Bomba', coolingCapacity: 'Capacidade Resfriamento', fanVoltage: 'Ventilador',
    connections: 'Conexões', sections: 'Seções'
  };

  const specs = product.specifications || {};
  const customSpecs = specs.custom || {};
  const mainSpecs = Object.entries(specs).filter(([k]) => k !== 'custom' && specs[k]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/produtos')} className="flex items-center text-[#055509] hover:text-[#FFB025] transition-colors">
            <ArrowLeft size={20} className="mr-2" />Voltar para Produtos
          </button>
        </div>
      </div>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="sticky top-24">
                <Card className="overflow-hidden border-2 border-gray-200">
                  <div className="relative bg-gray-100">
                    <img src={currentImage} alt={product.name} className="w-full h-auto object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSIyMDAiIHk9IjIwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Q0EzQUYiPlNlbSBJbWFnZW08L3RleHQ+PC9zdmc+'; }} />
                    <div className="absolute top-4 left-4"><Badge className="bg-[#055509] text-white text-sm">{product.category}</Badge></div>
                  </div>
                </Card>
                {images.length > 1 && (
                  <div className="flex gap-2 mt-3">
                    {images.map((img, i) => (
                      <button key={i} onClick={() => setSelectedImage(i)} className={`w-16 h-16 rounded border-2 overflow-hidden ${selectedImage === i ? 'border-[#055509]' : 'border-gray-200'}`}>
                        <img src={resolveImageUrl(img)} alt={`Thumb ${i}`} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSIyMDAiIHk9IjIwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Q0EzQUYiPlNlbSBJbWFnZW08L3RleHQ+PC9zdmc+'; }} />
                      </button>
                    ))}
                  </div>
                )}
                <Card className="mt-6 bg-gradient-to-br from-[#055509] to-[#0a7a0f] text-white border-none">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">Solicitar Cotação</h3>
                    <p className="text-gray-200 text-sm mb-4">Nossa equipe técnica responde em até 2 horas com disponibilidade e prazo de entrega</p>
                    <Button size="lg" onClick={handleQuoteClick} className="w-full bg-[#FFB025] text-[#055509] hover:bg-white hover:text-[#055509] font-bold">
                      <MessageCircle className="mr-2" size={20} />Solicitar Cotação no WhatsApp
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div>
              <div className="mb-8">
                <div className="text-sm font-mono text-[#FFB025] font-semibold mb-2">CÓDIGO: {product.code}</div>
                <h1 className="text-4xl font-bold text-[#055509] mb-4">{product.name}</h1>
                <p className="text-xl text-gray-600 leading-relaxed">{product.shortDescription}</p>
                {product.fullDescription && <p className="text-gray-600 leading-relaxed mt-4">{product.fullDescription}</p>}
              </div>

              <Separator className="my-6" />

              {(product.applications || []).length > 0 && (
                <>
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4"><Wrench className="text-[#FFB025]" size={24} /><h2 className="text-2xl font-bold text-[#055509]">Aplicações</h2></div>
                    <div className="grid grid-cols-1 gap-2">
                      {product.applications.map((app, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                          <Check className="text-[#FFB025] flex-shrink-0" size={20} /><span className="text-gray-700">{app}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Separator className="my-6" />
                </>
              )}

              {(mainSpecs.length > 0 || Object.keys(customSpecs).length > 0) && (
                <>
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4"><FileText className="text-[#FFB025]" size={24} /><h2 className="text-2xl font-bold text-[#055509]">Especificações Técnicas</h2></div>
                    <Card className="border-2 border-gray-200">
                      <CardContent className="p-6">
                        <div className="space-y-3">
                          {mainSpecs.map(([key, value]) => (
                            <div key={key} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
                              <span className="text-gray-600 font-medium">{specLabels[key] || key}:</span>
                              <span className="text-[#055509] font-semibold text-right ml-4">{value}</span>
                            </div>
                          ))}
                          {Object.entries(customSpecs).map(([key, value]) => (
                            <div key={key} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
                              <span className="text-gray-600 font-medium">{key}:</span>
                              <span className="text-[#055509] font-semibold text-right ml-4">{value}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <Separator className="my-6" />
                </>
              )}

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Package className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-bold text-blue-900 mb-2">{'Informa\u00e7\u00f5es Importantes'}</h3>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>{product.productType === 'importado' ? 'Produto importado' : 'Produto original'}</li>
                        <li>{'Garantia de aplica\u00e7\u00e3o correta'}</li>
                        <li>{'Envio para todo o Brasil e Exterior'}</li>
                        <li>{'Suporte t\u00e9cnico especializado'}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ProductDetail;
